(function () {
  const STORAGE_KEY = 'pv-province';
  const MANUAL_STORAGE_KEY = 'pv-province-manual';
  const DEFAULT_CACHE_DAYS = 7;
  const ONTARIO_CODE = 'ON';
  const OTHER_CODE = 'OTHER';

  // All logging is routed through the shared debug gate in province-routing.js so that
  // API responses / IP context are NEVER logged unless debug is explicitly enabled.
  function log(...args) {
    if (window.PVRouting && typeof window.PVRouting.log === 'function') {
      window.PVRouting.log('(detection)', ...args);
    }
  }

  function getConfig() {
    const config = (window.theme && window.theme.provinceFiltering) || {};
    return {
      enabled: Boolean(config.enabled),
      apiUrl: config.ipApiUrl || 'https://api.ipgeolocation.io/v3/ipgeo?apiKey=792846dcbf984f7ba15bd65b42377892',
      fallbackApiUrl: config.fallbackApiUrl || 'https://ipwho.is/',
      cacheDays: config.cacheDays != null ? Number(config.cacheDays) : DEFAULT_CACHE_DAYS
    };
  }

  function isCanadaContext() {
    const country = (window.theme && window.theme.localizationCountry) || '';
    return String(country).toUpperCase() === 'CA';
  }

  function getTestMode() {
    const mode = (window.theme && window.theme.provinceTestMode) || 'off';
    return mode;
  }

  // cacheDays === 0 → TEST MODE: use sessionStorage (one request per browser session, no timestamp).
  // cacheDays > 0   → PRODUCTION: use localStorage with timestamp-based expiry.
  function isTestMode(cacheDays) {
    return cacheDays === 0;
  }

  function getStorage(cacheDays) {
    return isTestMode(cacheDays) ? sessionStorage : localStorage;
  }

  function safeJSONParse(value) {
    try {
      return JSON.parse(value);
    } catch (error) {
      return null;
    }
  }

  function normalizeProvince(regionCode, countryCode) {
    const normalizedCountry = String(countryCode || '').toUpperCase();
    const normalizedRegion = String(regionCode || '').toUpperCase();
    if (normalizedCountry === 'CA' && normalizedRegion === ONTARIO_CODE) {
      return ONTARIO_CODE;
    }
    return OTHER_CODE;
  }

  // Native selects in the localization form sit under a styled label; mirror the value.
  function syncSelectLabel(select) {
    const label = select.parentElement && select.parentElement.querySelector('[data-loc-select-label]');
    const option = select.options[select.selectedIndex];
    if (label && option) label.textContent = option.textContent.trim();
  }

  function syncProvinceSelects(provinceCode) {
    const value = provinceCode === ONTARIO_CODE ? 'ON' : 'OTHER';
    document.querySelectorAll('[data-province-select]').forEach((select) => {
      if (select.value !== value) select.value = value;
      syncSelectLabel(select);
    });
  }

  function applyProvince(payload) {
    const province = payload && payload.province != null ? payload.province : OTHER_CODE;
    const country = payload && payload.country ? payload.country : '';

    // provinceState (additive — existing consumers ignore it): 'ca' once resolved to a
    // Canadian visitor, otherwise 'non_ca'. The filter's nav guard only skips on 'pending',
    // so IP-mode (ON store) behaviour is unchanged.
    const state =
      payload && payload.state
        ? payload.state
        : String(country).toUpperCase() === 'CA'
        ? 'ca'
        : 'non_ca';

    document.body.dataset.province = province;
    document.body.dataset.provinceCountry = country;
    document.body.dataset.provinceState = state;

    window.PrimeVapeProvince = {
      province,
      country,
      state,
      isOntario: province === ONTARIO_CODE
    };

    syncProvinceSelects(province);

    document.dispatchEvent(
      new CustomEvent('on:province:detected', {
        detail: window.PrimeVapeProvince
      })
    );
  }

  // Single DOM-apply entry point shared with province-routing.js (popup / manual / inbound).
  // province-routing.js loads first but only calls this later, by which time it is defined.
  window.PVProvinceApply = function (payload) {
    applyProvince(payload);
    writeVisitorCartAttr(payload, (payload && payload.source) || 'routing');
  };

  function buildVisitorContextValue(provincePayload, source) {
    const market = (window.theme && window.theme.currentMarketHandle) || '';
    const localeCountry = (window.theme && window.theme.localizationCountry) || '';
    const ipCountry = String((provincePayload && provincePayload.country) || '').toUpperCase();
    const rawRegion = String((provincePayload && provincePayload.rawRegion) || '').toUpperCase();
    const ipProvince = (provincePayload && provincePayload.province) || OTHER_CODE;
    const ipAddr = (provincePayload && provincePayload.ip) || '';

    // e.g. CA/QC/OTHER, UA/KYIV/OTHER, CA/ON/ON
    const locParts = [ipCountry || 'unknown'];
    if (rawRegion) locParts.push(rawRegion);
    locParts.push(ipProvince);

    const parts = [];
    if (ipAddr) parts.push('ip:' + ipAddr);
    parts.push('ip_loc:' + locParts.join('/'));
    parts.push('market:' + (market || 'unknown'));
    parts.push('locale_country:' + (localeCountry || 'unknown'));
    parts.push('src:' + source);
    return parts.join(' | ');
  }

  function writeVisitorCartAttr(provincePayload, source) {
    const value = buildVisitorContextValue(provincePayload, source);
    try {
      localStorage.setItem('pv-visitor-context', value);
    } catch (e) {}
    document.querySelectorAll('[name="attributes[_visitor_context]"]').forEach(function (el) {
      el.value = value;
    });
  }

  function isManualProvinceSet() {
    try {
      return localStorage.getItem(MANUAL_STORAGE_KEY) === 'true';
    } catch (e) {
      return false;
    }
  }

  function readCachedProvince(cacheDays) {
    const storage = getStorage(cacheDays);
    let raw;
    try {
      raw = storage.getItem(STORAGE_KEY);
    } catch (error) {
      return null;
    }
    if (!raw) return null;

    const parsed = safeJSONParse(raw);
    if (!parsed || !parsed.province) return null;

    // Test mode (sessionStorage): no expiry check — session itself is the TTL.
    if (isTestMode(cacheDays)) return parsed;

    // Production mode: check timestamp expiry (skip for manual — manual has no timestamp requirement).
    if (isManualProvinceSet()) return parsed;
    if (!parsed.timestamp) return null;
    const cacheMs = cacheDays * 24 * 60 * 60 * 1000;
    if (Date.now() - Number(parsed.timestamp) > cacheMs) return null;

    return parsed;
  }

  function writeCachedProvince(payload, cacheDays) {
    const storage = getStorage(cacheDays);
    const entry = {
      province: payload.province,
      country: payload.country || '',
      rawRegion: payload.rawRegion || '',
      ip: payload.ip || '',
    };

    // Only store timestamp in production mode.
    if (!isTestMode(cacheDays)) {
      entry.timestamp = Date.now();
    }

    try {
      storage.setItem(STORAGE_KEY, JSON.stringify(entry));
    } catch (error) {
      // Ignore storage write errors (private mode / quota exceeded).
    }
  }

  async function fetchLocation(url) {
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        Accept: 'application/json'
      }
    });

    if (!response.ok) {
      throw new Error('Province API request failed');
    }

    return response.json();
  }

  function mapApiPayload(payload) {
    if (!payload || typeof payload !== 'object') {
      return { province: OTHER_CODE, country: '', rawRegion: '', ip: '' };
    }

    // Support both ipgeolocation.io format ({ location: { country_code2, state_code } })
    // and legacy ipapi.co / ipwho.is format ({ country_code, region_code }).
    let country = '';
    let region = '';

    if (payload.location && typeof payload.location === 'object') {
      // ipgeolocation.io: state_code is "CA-ON" — extract the part after the dash.
      country = payload.location.country_code2 || '';
      const stateCode = payload.location.state_code || '';
      region = stateCode.includes('-') ? stateCode.split('-').pop() : stateCode;
    } else {
      country = payload.country_code || payload.country || '';
      region = payload.region_code || payload.region || '';
    }

    return {
      province: normalizeProvince(region, country),
      country: String(country || '').toUpperCase(),
      rawRegion: String(region || '').toUpperCase(),
      ip: String(payload.ip || ''),
    };
  }

  function logApi(testMode, ...args) {
    if (window.PVRouting && typeof window.PVRouting.log === 'function') {
      window.PVRouting.log('(detection:api)', ...args);
    }
  }

  async function detectProvince(config) {
    const testMode = isTestMode(config.cacheDays);

    try {
      const primaryPayload = await fetchLocation(config.apiUrl);
      logApi(testMode, 'Primary API used:', config.apiUrl);
      logApi(testMode, 'Response:', primaryPayload);
      const result = mapApiPayload(primaryPayload);
      logApi(testMode, 'Detected:', result);
      return result;
    } catch (primaryError) {
      logApi(testMode, 'Primary API failed:', primaryError.message);
      try {
        const fallbackPayload = await fetchLocation(config.fallbackApiUrl);
        logApi(testMode, 'Fallback API used:', config.fallbackApiUrl);
        logApi(testMode, 'Response:', fallbackPayload);
        const result = mapApiPayload(fallbackPayload);
        logApi(testMode, 'Detected:', result);
        return result;
      } catch (fallbackError) {
        logApi(testMode, 'Fallback API also failed:', fallbackError.message, '→ defaulting to OTHER');
        return { province: OTHER_CODE, country: '' };
      }
    }
  }

  async function init() {
    const config = getConfig();
    const testModeVal = getTestMode();
    const isCA = isCanadaContext();

    log('init', { enabled: config.enabled, localizationCountry: (window.theme && window.theme.localizationCountry) || 'N/A', testMode: testModeVal, isCanada: isCA });

    if (!config.enabled) {
      log('Province filtering disabled in settings, skipping.');
      return;
    }

    // --- Mode-aware routing (province-routing.js) -------------------------------------
    // Both modes consume an inbound choice (param or cookie) BEFORE any IP work.
    const routing = window.PVRouting;
    const mode = routing && routing.mode;
    if (routing && routing.ready && testModeVal === 'off') {
      const inbound = routing.consumeInbound();
      if (inbound && inbound.source === 'param') {
        // A province in the URL that belongs to the OTHER store (e.g. ?pv_province=QC opened
        // on the ON store) → send the visitor there, same page, once. The redirect carries
        // pv_hop=1, so a page that already hopped (or a crawler) just applies it here.
        if (routing.inboundAction && routing.inboundAction(inbound) === 'redirect') {
          log('Inbound province from param belongs to another store, redirecting:', inbound.code);
          const routed = routing.setProvince(inbound.code, { allowRedirect: true, confirmCart: false, source: 'inbound:param' });
          if (routed && routed.action === 'redirect') return;
        } else {
          // Same store, or just arrived via cross-store redirect → apply here, never redirect again.
          log('Inbound province from param (applying locally):', inbound.code);
          routing.setProvince(inbound.code, { allowRedirect: false, source: 'inbound:param' });
        }
        // A Canadian province was chosen, but this store still remembers a foreign country
        // (its own localization cookie) → switch this store to Canada, otherwise the next
        // page load would send the visitor to the international store again.
        if (!isCA && routing.switchToCanada) routing.switchToCanada();
        return;
      }
      if (!isCA) {
        // Non-CA visitor. This MUST run before the stored province cookie: a visitor who
        // picked another country keeps their old pv_province (e.g. ON), and routing by that
        // cookie would pin them to a Canadian store. Stores that don't serve international
        // orders send them to config.internationalStore (same page, loop-guarded, crawlers
        // exempt). The cookie itself is kept for when they switch back to Canada.
        // Exception: this tab arrived from the other store with a Canadian province, and this
        // store still remembers a foreign country → switch this store to Canada (loop-guarded)
        // instead of bouncing the visitor back to the international store.
        // Same for a saved Canadian province when the visitor never picked a foreign country
        // themselves (the market was set automatically: geo-IP, VPN, travel) → back to Canada,
        // routed by the province: the other store directly (the hop sets Canada there), or
        // this store switched to Canada (the reload then routes by the cookie).
        const explicitForeign = routing.explicitForeignCountry && routing.explicitForeignCountry();
        if (routing.arrivedViaCanadianHop && routing.arrivedViaCanadianHop()) {
          log('Non-CA market but arrived via a Canadian province hop → switching to Canada.');
          if (routing.switchToCanada && routing.switchToCanada().action === 'redirect') return;
        } else if (inbound && !explicitForeign) {
          log('Non-CA market set automatically, saved province', inbound.code, '→ back to Canada.');
          const target = routing.resolveTargetStore(inbound.code);
          if (target && target !== routing.currentStore) {
            const routed = routing.setProvince(inbound.code, { allowRedirect: true, confirmCart: false, source: 'cookie' });
            if (routed && routed.action === 'redirect') return;
          }
          if (routing.switchToCanada && routing.switchToCanada().action === 'redirect') return;
        } else if (routing.routeInternational && routing.routeInternational().action === 'redirect') {
          return;
        }
        if (mode === 'popup') {
          // Non-CA market (e.g. international on the QC store): no province step, but the
          // catalog/sections/menu must still resolve to FED (OTHER) — never "all products".
          log('popup mode, non-CA market → FED (OTHER), age-only (no province step).');
          const nonCaPayload = {
            province: OTHER_CODE,
            country: String((window.theme && window.theme.localizationCountry) || '').toUpperCase(),
            state: 'non_ca'
          };
          applyProvince(nonCaPayload);
          writeVisitorCartAttr(nonCaPayload, 'popup_non_ca');
          return;
        }
        // ip mode → fall through; the IP flow below applies OTHER for non-CA markets.
      } else if (inbound) {
        // Returning Canadian visitor with a stored choice → route to the correct store
        // (loop-guarded; no cart confirm on automatic routing).
        log('Province from cookie (routing to correct store if needed):', inbound.code);
        routing.setProvince(inbound.code, { allowRedirect: true, confirmCart: false, source: 'cookie' });
        return;
      } else if (mode === 'popup') {
        // CA market, no stored choice → pending; the popup will call PVRouting.setProvince().
        log('popup mode, CA market → pending province, awaiting popup choice.');
        document.body.dataset.province = '';
        document.body.dataset.provinceState = 'pending';
        return;
      }
      // mode === 'ip' (or null/fail-closed) → fall through to the existing IP flow below.
    }

    if (testModeVal === 'outside_canada') {
      log('Test mode: outside_canada → applying OTHER (simulated non-CA market).');
      const payload = { province: OTHER_CODE, country: 'US' };
      applyProvince(payload);
      writeVisitorCartAttr(payload, 'test:outside_canada');
      return;
    }

    if (testModeVal === 'ON' || testModeVal === 'OTHER') {
      log('Test mode: applying hardcoded province', testModeVal);
      const testPayload = { province: testModeVal, country: 'CA' };
      applyProvince(testPayload);
      writeVisitorCartAttr(testPayload, 'test:hardcoded');
      return;
    }

    if (!isCA) {
      const country = String((window.theme && window.theme.localizationCountry) || '').toUpperCase();
      log('Non-CA market → applying OTHER without API call.', { country });
      const nonCaPayload = { province: OTHER_CODE, country };
      applyProvince(nonCaPayload);
      writeVisitorCartAttr(nonCaPayload, 'non_ca_market');
      return;
    }

    const manualSet = isManualProvinceSet();
    if (manualSet) {
      try {
        const raw = localStorage.getItem(STORAGE_KEY);
        const cached = raw ? safeJSONParse(raw) : null;
        if (cached && cached.province) {
          log('Using manually selected province from localStorage:', cached);
          applyProvince(cached);
          writeVisitorCartAttr(cached, 'manual');
          return;
        }
      } catch (e) {
        log('Failed to read manual province from localStorage', e);
      }
    }

    const testMode = isTestMode(config.cacheDays);
    if (testMode) log('Cache test mode (cacheDays=0) — using sessionStorage');

    let cached = null;
    try {
      cached = readCachedProvince(config.cacheDays);
    } catch (error) {
      cached = null;
    }

    if (cached) {
      log('Loaded from cache:', cached);
      applyProvince(cached);
      writeVisitorCartAttr(cached, 'cache');
      return;
    }

    log('No cache — fetching location from API.');
    const detected = await detectProvince(config);
    if (String((detected && detected.country) || '').toUpperCase() !== 'CA') {
      log('API returned non-CA country:', detected.country, '— applying OTHER province for filtering.');
      const nonCaPayload = {
        province: OTHER_CODE,
        country: String((detected && detected.country) || '').toUpperCase(),
        rawRegion: (detected && detected.rawRegion) || '',
        ip: (detected && detected.ip) || ''
      };
      applyProvince(nonCaPayload);
      writeVisitorCartAttr(detected, 'api:non_ca');
      return;
    }
    writeCachedProvince(detected, config.cacheDays);
    applyProvince(detected);
    writeVisitorCartAttr(detected, 'api');
  }

  window.__setProvinceManually = function (provinceCode) {
    if (!isCanadaContext()) {
      log('Manual override ignored on non-CA market.');
      return;
    }
    const payload = { province: provinceCode === 'ON' ? 'ON' : 'OTHER', country: 'CA' };
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
      localStorage.setItem(MANUAL_STORAGE_KEY, 'true');
      applyProvince(payload);
      log('Province set manually:', payload);
    } catch (e) {
      console.error('[Province Detection] Failed to set manual province', e);
    }
  };

  document.addEventListener('change', function (e) {
    if (!e.target || !e.target.matches || !e.target.matches('[data-province-select]')) return;
    if (!isCanadaContext()) return;
    const val = e.target.value;
    if (val === 'ON' || val === 'OTHER') window.__setProvinceManually(val);
  });

  function handleCheckoutRedirect() {
    var url = new URL(window.location.href);
    var province = url.searchParams.get('province');
    var clear = url.searchParams.get('clear');

    if (!province || clear !== 'true') return;

    var normalizedProvince = String(province).toUpperCase();
    if (normalizedProvince !== ONTARIO_CODE && normalizedProvince !== OTHER_CODE) return;

    log('handleCheckoutRedirect: province=', normalizedProvince, '— clearing cart');

    var payload = { province: normalizedProvince, country: 'CA' };
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
      localStorage.setItem(MANUAL_STORAGE_KEY, 'true');
    } catch (e) {
      // Ignore storage errors.
    }
    applyProvince(payload);

    // Remove ?province=&clear= from the address bar so a page refresh won't re-trigger this.
    url.searchParams.delete('province');
    url.searchParams.delete('clear');
    var cleanUrl = url.pathname + (url.search || '') + (url.hash || '');
    history.replaceState(null, '', cleanUrl);

    // Clear the cart, then re-render the cart icon bubble.
    fetch('/cart/clear.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json'
      }
    })
      .then(function (res) { return res.json(); })
      .then(function () {
        log('handleCheckoutRedirect: cart cleared — refreshing cart icon bubble');
        return fetch('/?sections=cart-icon-bubble');
      })
      .then(function (res) { return res.json(); })
      .then(function (data) {
        var el = document.getElementById('cart-icon-bubble');
        if (el && data['cart-icon-bubble']) {
          var tmpl = document.createElement('template');
          tmpl.innerHTML = data['cart-icon-bubble'];
          var content = tmpl.content.querySelector('.shopify-section');
          if (content) el.innerHTML = content.innerHTML;
        }
        log('handleCheckoutRedirect: cart icon bubble refreshed');
      })
      .catch(function (err) {
        console.error('[Province Detection] handleCheckoutRedirect error:', err);
      });
  }

  document.addEventListener('DOMContentLoaded', function () {
    handleCheckoutRedirect();
    init();
    var p = document.body.dataset.province;
    if (p === 'ON' || p === 'OTHER') syncProvinceSelects(p);
  });
})();
